/**
 * assignee_picker_controller.js — choose who goes on a visit (Stimulus).
 *
 * One instance per templates/components/_assignee_picker.html, which appears in
 * both schedule modals. The controller owns three things and nothing else:
 *
 *   1. Fetching the team roster (GET /api/team/members) each time a modal
 *      opens the picker, and rendering it as a checkbox list. It is not
 *      server-rendered because the partial ships in base.html on every page
 *      and most pages never open the modal.
 *   2. Lead selection — a star per row, radio-like: picking one clears the
 *      others, and unchecking a member clears their lead mark.
 *   3. Reset / preload, so opening the modal for a NEW visit starts empty and
 *      opening it to EDIT one starts from that visit's existing crew.
 *
 * Reading the selection back out is deliberately NOT here: both submit paths
 * (calendar.bundle's create-event.js and this bundle's schedule_modal
 * controller) share static/js/utils/assignee-selection.js instead, so the DOM
 * contract has one definition.
 *
 * A plain checkbox list rather than a Choices-wrapped <select multiple>: the
 * options arrive from a fetch, so a Choices instance would have to be built
 * empty and refilled through setChoices, and tap targets beat a combobox on the
 * phone this is mostly used from.
 *
 * The roster must not be held in module or class state. Turbo never reloads
 * modules, so anything kept there lives for the whole tab: a member invited
 * after the first fetch would never appear in an admin's open tab.
 */
import { Controller } from "@hotwired/stimulus";

const MEMBERS_URL = '/api/team/members';

/** The current roster, or null when the fetch failed. */
function fetchRoster() {
    return fetch(MEMBERS_URL, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
        .then((data) => data.members || data.data || [])
        .catch(() => null);
}

export default class extends Controller {
    static targets = ["list", "filter", "summary"];

    /** Fetch and render the roster; a failure leaves an honest message, not an empty list. */
    async load() {
        const members = await fetchRoster();
        if (!this.hasListTarget) return;      // disconnected while in flight

        if (!members) {
            this.listTarget.innerHTML =
                '<p class="text-muted small mb-0">Could not load the team. Reopen to retry.</p>';
            return;
        }
        if (!members.length) {
            this.listTarget.innerHTML =
                '<p class="text-muted small mb-0">No other team members yet.</p>';
            return;
        }

        this.listTarget.innerHTML = '';
        members.forEach((member) => this.listTarget.appendChild(this._row(member)));
        if (this.hasFilterTarget) this.filterTarget.hidden = members.length < 6;
        this._refreshSummary();
    }

    /** One member row: a checkbox, the name, and a lead star. */
    _row(member) {
        const row = document.createElement('label');
        row.className = 'assignee-option';
        row.dataset.name = (member.display_name || member.email || '').toLowerCase();

        const box = document.createElement('input');
        box.type = 'checkbox';
        box.className = 'form-check-input me-2';
        box.value = member.user_id;
        box.setAttribute('data-assignee-check', '');
        box.addEventListener('change', () => this._onToggle(box));
        row.appendChild(box);

        const name = document.createElement('span');
        name.className = 'flex-grow-1 text-truncate';
        name.textContent = member.display_name || member.email;
        row.appendChild(name);

        row.appendChild(this._leadStar(member.user_id));
        return row;
    }

    _leadStar(userId) {
        const star = document.createElement('button');
        star.type = 'button';
        star.className = 'btn btn-link btn-sm p-0 assignee-lead-toggle';
        star.title = 'Mark as lead';
        star.innerHTML = '<i class="bi bi-star"></i>';
        star.setAttribute('data-assignee-lead', '');
        star.dataset.userId = userId;
        star.addEventListener('click', (event) => {
            event.preventDefault();
            this._setLead(star);
        });
        return star;
    }

    /** Unchecking someone cannot leave them as the lead. */
    _onToggle(box) {
        if (!box.checked) {
            const star = box.closest('.assignee-option').querySelector('[data-assignee-lead]');
            if (star) this._markLead(star, false);
        }
        this._refreshSummary();
    }

    /** Radio-like: at most one lead, and clicking the current lead clears it. */
    _setLead(star) {
        const wasLead = star.classList.contains('is-lead');
        this.element.querySelectorAll('[data-assignee-lead]')
            .forEach((other) => this._markLead(other, false));
        if (wasLead) return;

        const box = star.closest('.assignee-option').querySelector('[data-assignee-check]');
        if (box && !box.checked) {
            box.checked = true;      // leading a visit implies being on it
        }
        this._markLead(star, true);
        this._refreshSummary();
    }

    _markLead(star, on) {
        star.classList.toggle('is-lead', on);
        star.innerHTML = on ? '<i class="bi bi-star-fill"></i>' : '<i class="bi bi-star"></i>';
    }

    /** Narrow the visible rows as the user types. */
    filter() {
        const term = this.filterTarget.value.trim().toLowerCase();
        this.element.querySelectorAll('.assignee-option').forEach((row) => {
            row.hidden = !!term && !row.dataset.name.includes(term);
        });
    }

    _refreshSummary() {
        if (!this.hasSummaryTarget) return;
        const count = this.element.querySelectorAll('[data-assignee-check]:checked').length;
        this.summaryTarget.textContent = count ? `${count} selected` : 'Nobody yet';
    }

    /**
     * Start from `userIds` (empty for a new visit). Called by whichever modal
     * is opening, via the `assignee-picker:set` event so the caller needs no
     * handle on this instance. Opening is also when the roster is refetched,
     * so the list is never older than the modal showing it.
     */
    async set(event) {
        this._seed = {
            wanted: (event && event.detail && event.detail.userIds) || [],
            lead: (event && event.detail && event.detail.leadUserId) || null,
        };
        this._applySeed();
        await this.load();
        // Re-apply the LATEST seed: a reopen during the fetch replaced it.
        if (this.hasListTarget) this._applySeed();
    }

    _applySeed() {
        const { wanted, lead } = this._seed;
        this.element.querySelectorAll('[data-assignee-check]').forEach((box) => {
            box.checked = wanted.includes(parseInt(box.value, 10));
        });
        this.element.querySelectorAll('[data-assignee-lead]').forEach((star) => {
            this._markLead(star, lead !== null && parseInt(star.dataset.userId, 10) === lead);
        });
        if (this.hasFilterTarget) {
            this.filterTarget.value = '';
            this.filter();
        }
        this._refreshSummary();
    }
}
